import type { FormattedError } from '@shared/utils/RequestUtils';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useProductCosts, useProductsWithoutCost } from '../../hooks';
import type { ProfitabilityProductCost } from '../../models/Profitability';
import { ProductCostsList, type ProductCostsListView } from '../display/ProductCostsList';
import { ProductCostsModal, type ProductCostsModalProduct } from '../interaction/ProductCostsModal';

function isFormattedError(e: unknown): e is FormattedError {
  return typeof e === 'object' && e !== null && 'message' in e && typeof (e as FormattedError).message === 'string';
}

function matchesProductCostSearch(row: ProfitabilityProductCost, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  const name = row.product_name?.toLowerCase() ?? '';
  const code = row.product_code?.toLowerCase() ?? '';
  const id = String(row.product_id);
  return name.includes(q) || code.includes(q) || id.includes(q);
}

export const ProductCostsPage = () => {
  const { t, i18n } = useTranslation('profitabilityModule');
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'es-ES';
  const navigate = useNavigate();
  const currency = useCurrency();

  const [view, setView] = useState<ProductCostsListView>('pending');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [modalProduct, setModalProduct] = useState<ProductCostsModalProduct | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const costsQuery = useProductCosts(true);
  const pendingQuery = useProductsWithoutCost(true, {
    search: view === 'pending' ? debouncedSearch : '',
    page: view === 'pending' ? page : 1,
    per_page: view === 'pending' ? 20 : 1,
  });

  const baseCostLabel = t('product_costs.base_cost_label');

  const handleModalSaved = () => {
    if (view === 'pending') setView('configured');
  };

  const breadcrumbs = useMemo(
    () => [{ label: t('title'), path: '/reports/profitability' }, { label: t('product_costs.page_title') }],
    [t],
  );

  const headerActions = useMemo(
    () => [
      {
        id: 'profitability-back-to-report',
        label: t('product_costs.back_to_overview'),
        onClick: () => navigate('/reports/profitability'),
        icon: <ArrowLeft className="h-4 w-4" />,
        variant: 'outline' as const,
      },
    ],
    [navigate, t],
  );

  usePageNavigation({ breadcrumbs, headerActions });

  const activeQuery = view === 'pending' ? pendingQuery : costsQuery;
  const errorDescription = (() => {
    const e = activeQuery.error;
    if (!e || !isFormattedError(e)) return null;
    if (e.statusCode === 403) return e.message || t('errors.forbidden');
    return e.message || t('errors.generic');
  })();

  const hasBlockingError = !!errorDescription && activeQuery.isError && !activeQuery.data && !activeQuery.isLoading;

  const configuredRows = useMemo(() => {
    const all = costsQuery.data?.data ?? [];
    return all.filter((row) => matchesProductCostSearch(row, debouncedSearch));
  }, [costsQuery.data?.data, debouncedSearch]);

  const pendingRows = view === 'pending' ? (pendingQuery.data?.data ?? []) : [];
  const pendingMeta = pendingQuery.data?.meta;

  return (
    <div className="space-y-6 px-6 py-4" data-testid="profitability-product-costs-page">
      {hasBlockingError ? (
        <EmptyView
          type="emptyState"
          icon={EMPTY_VIEW_ICONS.ORDERS}
          title={t('empty.error.title')}
          description={errorDescription ?? t('errors.generic')}
        />
      ) : (
        <SectionBox data-testid="profitability-product-costs-main">
          <div className="mb-4 space-y-1">
            <h2 className="text-lg font-semibold text-title">{t('product_costs.page_title')}</h2>
            <p className="text-sm text-muted-foreground">{t('product_costs.description')}</p>
          </div>

          <ProductCostsList
            view={view}
            onViewChange={(next) => {
              setView(next);
              setPage(1);
            }}
            search={searchInput}
            onSearchChange={setSearchInput}
            pendingTotal={pendingMeta?.total}
            pendingRows={pendingRows}
            configuredRows={configuredRows}
            isLoading={view === 'pending' ? pendingQuery.isLoading : costsQuery.isLoading}
            isError={view === 'pending' ? pendingQuery.isError : costsQuery.isError}
            locale={locale}
            currency={currency}
            baseCostLabel={baseCostLabel}
            pagination={
              pendingMeta
                ? {
                    currentPage: pendingMeta.current_page,
                    lastPage: pendingMeta.last_page,
                    total: pendingMeta.total,
                    isFetching: pendingQuery.isFetching,
                    onPrev: () => setPage((p) => Math.max(1, p - 1)),
                    onNext: () => setPage((p) => p + 1),
                  }
                : undefined
            }
            onManageProduct={setModalProduct}
          />
        </SectionBox>
      )}

      <ProductCostsModal
        open={modalProduct != null}
        onOpenChange={(open) => !open && setModalProduct(null)}
        product={modalProduct}
        locale={locale}
        currency={currency}
        onSaved={handleModalSaved}
      />
    </div>
  );
};
